import {
  createContext,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from "react";
import {
  getCurrentUser,
  login as apiLogin,
  logout as apiLogout,
} from "@/services/auth";
import { onUnauthorized } from "@/services/api";
import type { User, LoginRequest } from "@/types/auth";

const AUTH_USER_KEY = "auth_user";

function getStoredUser(): User | null {
  try {
    const raw = localStorage.getItem(AUTH_USER_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as User;
  } catch {
    return null;
  }
}

function setStoredUser(user: User | null): void {
  if (user) {
    localStorage.setItem(AUTH_USER_KEY, JSON.stringify(user));
  } else {
    localStorage.removeItem(AUTH_USER_KEY);
  }
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (credentials: LoginRequest) => Promise<void>;
  logout: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextType | undefined>(
  undefined,
);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(() => getStoredUser());
  const [isLoading, setIsLoading] = useState(true);

  const setUserAndStorage = useCallback((next: User | null) => {
    setUser(next);
    setStoredUser(next);
  }, []);

  useEffect(() => {
    return onUnauthorized(() => setUserAndStorage(null));
  }, [setUserAndStorage]);

  useEffect(() => {
    // Validate session with backend; rehydrate from storage so we don't flash unauthenticated on reload
    getCurrentUser()
      .then(setUserAndStorage)
      .catch(() => setUserAndStorage(null))
      .finally(() => setIsLoading(false));
  }, [setUserAndStorage]);

  const login = async (credentials: LoginRequest) => {
    const loggedInUser = await apiLogin(credentials);
    setUserAndStorage(loggedInUser);
  };

  const logout = async () => {
    await apiLogout();
    setUserAndStorage(null);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isLoading,
        login,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
